/**
 * Tenant-derived compliance reports library
 * ============================================================================
 * Generates a believable list of report files per tenant. Names are tenant-
 * specific (country, year, scope), file sizes scale with workforce size, and
 * report types match the regulatory frameworks of the tenant's country.
 */

const COUNTRY_REPORT_TYPES = {
  Germany: [
    { template: 'eu-directive-standard',     name: 'EU Pay Transparency Report',     period: 'annual' },
    { template: 'germany-entgelttransparenz', name: 'Entgelttransparenz Report',      period: 'biannual' },
    { template: 'eu-directive-standard',     name: 'Article 9 Mandatory Disclosure', period: 'annual' },
  ],
  Spain: [
    { template: 'eu-directive-standard',  name: 'EU Pay Transparency Report',         period: 'annual' },
    { template: 'spain-registro-salarial', name: 'Registro Salarial, Real Decreto 902/2020', period: 'annual' },
    { template: 'eu-directive-standard',  name: 'Article 9 Mandatory Disclosure',     period: 'annual' },
  ],
  Switzerland: [
    { template: 'switzerland-loga',      name: 'LOGA Logib Analyse',                  period: 'annual' },
    { template: 'eu-directive-standard', name: 'EU-aligned Pay Transparency Report (voluntary)', period: 'annual' },
  ],
  'United Kingdom': [
    { template: 'uk-gpg-regulations',    name: 'Gender Pay Gap Report (GPG Regulations 2017)', period: 'annual' },
    { template: 'uk-equality-act',       name: 'Equality Act 2010 Pay Audit',         period: 'annual' },
    { template: 'eu-directive-standard', name: 'EU-aligned Report (continental subsidiaries)', period: 'annual' },
  ],
  France: [
    { template: 'france-index-egalite',  name: 'Index Égalité Professionnelle',       period: 'annual' },
    { template: 'eu-directive-standard', name: 'EU Pay Transparency Report',          period: 'annual' },
  ],
};

const STAFF = [
  'Sarah Johnson', 'Michael Weber', 'Emma Thompson', 'David Kim',
  'Lisa Anderson', 'Marco Bianchi', 'Anna Müller', 'Carlos Ruiz',
];

const stableHash = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) { h = ((h << 5) - h) + s.charCodeAt(i); h |= 0; }
  return Math.abs(h);
};

const PERIODS = [
  { code: 'q4', label: 'Q4', month: 12 },
  { code: 'q3', label: 'Q3', month: 9 },
  { code: 'q2', label: 'Q2', month: 6 },
  { code: 'q1', label: 'Q1', month: 3 },
  { code: 'h2', label: 'H2', month: 12 },
  { code: 'h1', label: 'H1', month: 6 },
];

const isoDateAtMonth = (year, month, day = 15) => {
  const d = new Date(year, month - 1, day, 10, 30);
  return d.toISOString();
};

export function buildComplianceReports(tenant) {
  const types = COUNTRY_REPORT_TYPES[tenant.country] || COUNTRY_REPORT_TYPES.Germany;
  const out = [];
  let counter = 1;

  // Estimate file size from workforce, bigger orgs produce bigger reports
  const baseFileSize = 1_200_000 + tenant.employees * 60;

  // For each report type, generate a few historical entries
  for (const type of types) {
    const periodsToGen = type.period === 'biannual'
      ? [{ year: tenant.reportingYear, period: 'h2' }, { year: tenant.reportingYear, period: 'h1' }, { year: tenant.reportingYear - 1, period: 'h2' }]
      : [{ year: tenant.reportingYear - 1, period: 'q4' }, { year: tenant.reportingYear - 1, period: 'q2' }, { year: tenant.reportingYear - 2, period: 'q4' }];

    for (const p of periodsToGen) {
      const periodMeta = PERIODS.find((x) => x.code === p.period);
      const seed = `${tenant.id}-${type.template}-${p.year}-${p.period}`;
      const isLatest = counter <= 2;
      const status = isLatest && p.year === tenant.reportingYear ? 'generated' : 'generated';

      out.push({
        id: `rpt-${tenant.id.slice(0, 4)}-${String(counter++).padStart(3, '0')}`,
        name: `${periodMeta.label} ${p.year} ${type.name}`,
        template: type.template,
        period: `${p.year}-${p.period}`,
        scope: tenant.name,
        status,
        createdAt: isoDateAtMonth(p.year, periodMeta.month + 1, 8 + (stableHash(seed) % 14)),
        fileSize: baseFileSize + (stableHash(seed) % 800_000),
        checksum: 'sha256:' + seed.slice(0, 12) + '...' + (stableHash(seed) % 9999),
        isImmutable: true,
        availableFormats: ['pdf', 'csv', 'xlsx'],
        createdBy: STAFF[stableHash(seed) % STAFF.length],
      });
    }
  }

  // Add one in-progress report for the current period
  const currentPeriodSeed = `${tenant.id}-current-${tenant.reportingYear}`;
  out.unshift({
    id: `rpt-${tenant.id.slice(0, 4)}-${String(counter++).padStart(3, '0')}`,
    name: `Q${Math.ceil((new Date().getMonth() + 1) / 3)} ${tenant.reportingYear} ${types[0].name} (draft)`,
    template: types[0].template,
    period: `${tenant.reportingYear}-q${Math.ceil((new Date().getMonth() + 1) / 3)}`,
    scope: tenant.name,
    status: 'processing',
    createdAt: new Date().toISOString(),
    fileSize: 0,
    checksum: null,
    isImmutable: false,
    availableFormats: [],
    createdBy: STAFF[stableHash(currentPeriodSeed) % STAFF.length],
  });

  return out;
}
